/**
 * Feature E05-F05 — "Admin: команды (sourcing)" request/response contracts.
 *
 * 1:1 port of the legacy n8n `manage_sourcing_teams` action router (group ST,
 * docs/source-n8n/reference/ST.md + sql-queries.md "ST: Get All / Execute Create
 * / Execute Update / Execute Delete"). The old SPA POSTed
 * `{ command:'manage_sourcing_teams', action, ...payload, session_token }` to one
 * webhook; the v2 transport is clean REST under `/admin/sourcing-teams` but the
 * SAME logical contract — the four CRUD actions over the `sourcing_teams` table
 * (a sourcer team = a `name` + a `members` JSONB array of { name, email }).
 *
 * Endpoints honoured by the feature route (`routes/admin-sourcing-teams.ts`):
 *   GET    /admin/sourcing-teams        → { success:true, teams: SourcingTeamDTO[] }  (get_teams)
 *   POST   /admin/sourcing-teams        → { success:true, team: SourcingTeamDTO }     (create_team)
 *   PATCH  /admin/sourcing-teams/:id    → { success:true }                            (update_team)
 *   DELETE /admin/sourcing-teams/:id    → { success:true }                            (delete_team)
 *
 * Parity notes carried over from the legacy slice (ST.md gotchas):
 *   - get_teams returns ONLY is_active=true, ORDER BY name (soft-deleted hidden).
 *   - members is a JSONB array of { name, email }; default [] when not supplied.
 *   - create returns the created row (RETURNING id, name, members); update returns
 *     a bare { success:true } (the legacy `Return Update` ignored RETURNING).
 *   - delete is a SOFT delete (UPDATE is_active=false) — NOT a hard DELETE; this is
 *     the key behavioural difference from the recruiter `teams` table (admin-teams).
 *
 * Hardening over the legacy slice (ST.md gotchas about manual quote-escaping,
 * string-interpolated `::jsonb` casts and `parseInt` coercion): v2 uses Prisma
 * parameterized writes (no string SQL) and STRICT integer validation of `:id` at
 * the route boundary. The legacy create allowed an empty `name` (no-op SELECT 1
 * masked as success) — v2 returns the honest "Name is required" validation error
 * (T02 acceptance #3).
 *
 * IDOR note: the legacy slice read no role/user from the session and let any
 * authenticated user CRUD any team. v2 still requires a valid session
 * (`app.authenticate`); the route mounts under the admin surface. No userId is
 * ever read from the body.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Member (one row of the members JSONB array — ST.md: { name, email })
// ---------------------------------------------------------------------------
/**
 * One sourcing-team member. Both `name` and `email` are plain strings; the UI
 * only persists rows where BOTH are non-empty (T04 acceptance #2 — empty rows in
 * the dialog are dropped before save). The legacy members blob was free-form
 * JSON, so the wire schema stays permissive (no email-format enforcement) to let
 * any pre-existing legacy row round-trip.
 */
export const sourcingTeamMemberSchema = z.object({
    name: z.string(),
    email: z.string(),
});
// ---------------------------------------------------------------------------
// Read DTO — ST: Get All projection
// ---------------------------------------------------------------------------
/**
 * One sourcing team, mirroring the legacy `ST: Get All` SELECT
 * (`SELECT id, name, members, is_active FROM sourcing_teams WHERE is_active=true
 * ORDER BY name`).
 */
export const sourcingTeamSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    /** JSONB array of { name, email }. */
    members: z.array(sourcingTeamMemberSchema),
    /** Always true on the wire (get_teams filters is_active=true). */
    is_active: z.boolean(),
});
/** GET /admin/sourcing-teams response — active teams, ORDER BY name. */
export const sourcingTeamsListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    teams: z.array(sourcingTeamSchema),
});
// ---------------------------------------------------------------------------
// Create — ST: Build/Execute Create
// ---------------------------------------------------------------------------
/** The validation message the legacy create masked but v2 surfaces honestly. */
export const SOURCING_TEAM_NAME_REQUIRED_MESSAGE = 'Name is required';
/**
 * POST /admin/sourcing-teams body. `name` is REQUIRED (legacy allowed empty via a
 * masked no-op; v2 returns the honest "Name is required"). `members` is optional
 * and defaults to `[]` (ST.md: members default []).
 */
export const createSourcingTeamSchema = z.object({
    name: z.string().trim().min(1, SOURCING_TEAM_NAME_REQUIRED_MESSAGE),
    members: z.array(sourcingTeamMemberSchema).optional(),
});
// ---------------------------------------------------------------------------
// Update — ST: Build/Execute Update (full replace, PUT semantics)
// ---------------------------------------------------------------------------
/**
 * PATCH /admin/sourcing-teams/:id body. Legacy `update_team` was a FULL replace
 * of name+members (not a partial patch); v2 keeps that — `name` is required and
 * `members` defaults to `[]` when omitted (the whole array is replaced).
 */
export const updateSourcingTeamSchema = z.object({
    name: z.string().trim().min(1, SOURCING_TEAM_NAME_REQUIRED_MESSAGE),
    members: z.array(sourcingTeamMemberSchema).optional(),
});
// ---------------------------------------------------------------------------
// Mutation responses
// ---------------------------------------------------------------------------
/** Create response — the created row (legacy `ST: Return Create` returned it). */
export const sourcingTeamCreateResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    team: sourcingTeamSchema,
});
/**
 * Update + delete response — bare success. Legacy `ST: Return Update` and
 * `ST: Return Delete` both answered exactly `{ success:true }`.
 */
export const sourcingTeamOkResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
//# sourceMappingURL=admin-sourcing-teams.js.map